// @ts-nocheck
import React, { useState } from 'react'
import { X, AlertTriangle, User, Building2, Phone, Mail, Tag, FileText } from 'lucide-react'

/**
 * 顧客マージモーダルコンポーネント
 * 2つの顧客レコードを比較し、マージ戦略を選択してマージを実行
 */
const CustomerMergeModal = ({ isOpen, onClose, primaryCustomer, secondaryCustomer, onMerge }) => {
  const [mergeStrategy, setMergeStrategy] = useState('primary')
  const [isLoading, setIsLoading] = useState(false)

  if (!isOpen || !primaryCustomer || !secondaryCustomer) return null

  const handleMerge = async () => {
    setIsLoading(true)
    try {
      await onMerge(primaryCustomer.customerId, secondaryCustomer.customerId, mergeStrategy)
      onClose()
    } catch (error) {
      console.error('マージエラー:', error)
      alert('マージに失敗しました: ' + (error.message || '不明なエラー'))
    } finally {
      setIsLoading(false)
    }
  }

  // フィールド比較コンポーネント
  const FieldComparison = ({ label, icon: Icon, primaryValue, secondaryValue, fieldKey }) => {
    const isDifferent = primaryValue !== secondaryValue
    const selectedValue = mergeStrategy === 'primary' ? primaryValue : secondaryValue

    return (
      <div className={`p-3 rounded-lg ${isDifferent ? 'bg-yellow-50 dark:bg-yellow-900/20' : 'bg-gray-50 dark:bg-gray-800'}`}>
        <div className="flex items-center gap-2 mb-2">
          <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
          {isDifferent && (
            <span className="text-xs text-yellow-600 dark:text-yellow-400 ml-auto">相違あり</span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className={`p-2 rounded ${mergeStrategy === 'primary' ? 'bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-500' : 'bg-white dark:bg-gray-700'}`}>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">プライマリ</div>
            <div className="text-sm text-gray-900 dark:text-gray-100 break-all">
              {primaryValue || <span className="text-gray-400 dark:text-gray-500">未設定</span>}
            </div>
          </div>
          <div className={`p-2 rounded ${mergeStrategy === 'secondary' ? 'bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-500' : 'bg-white dark:bg-gray-700'}`}>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">セカンダリ</div>
            <div className="text-sm text-gray-900 dark:text-gray-100 break-all">
              {secondaryValue || <span className="text-gray-400 dark:text-gray-500">未設定</span>}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // タグ比較コンポーネント
  const TagsComparison = () => {
    const primaryTags = primaryCustomer.tags || []
    const secondaryTags = secondaryCustomer.tags || []
    const allTags = [...new Set([...primaryTags, ...secondaryTags])]

    return (
      <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center gap-2 mb-2">
          <Tag className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">タグ</span>
          <span className="text-xs text-blue-600 dark:text-blue-400 ml-auto">自動統合</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {allTags.length > 0 ? (
            allTags.map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 text-xs rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
              >
                {tag}
              </span>
            ))
          ) : (
            <span className="text-sm text-gray-400 dark:text-gray-500">タグなし</span>
          )}
        </div>
      </div>
    )
  }

  // 統計情報比較
  const StatsComparison = () => {
    return (
      <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">統計情報（自動統合）</div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">プライマリ</div>
            <div className="text-gray-900 dark:text-gray-100">
              通話回数: {primaryCustomer.callCount || 0}回
            </div>
            <div className="text-gray-900 dark:text-gray-100">
              総通話時間: {Math.floor((primaryCustomer.totalCallDuration || 0) / 60)}分
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">セカンダリ</div>
            <div className="text-gray-900 dark:text-gray-100">
              通話回数: {secondaryCustomer.callCount || 0}回
            </div>
            <div className="text-gray-900 dark:text-gray-100">
              総通話時間: {Math.floor((secondaryCustomer.totalCallDuration || 0) / 60)}分
            </div>
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="text-xs text-blue-600 dark:text-blue-400">
            マージ後: 通話回数 {(primaryCustomer.callCount || 0) + (secondaryCustomer.callCount || 0)}回、
            総通話時間 {Math.floor(((primaryCustomer.totalCallDuration || 0) + (secondaryCustomer.totalCallDuration || 0)) / 60)}分
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">顧客レコードのマージ</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              2つの顧客レコードを統合します。マージ戦略を選択してください。
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            disabled={isLoading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* 警告メッセージ */}
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800 dark:text-yellow-200">
              <p className="font-medium mb-1">この操作は取り消せません</p>
              <p>セカンダリレコードは論理削除され、すべての通話履歴がプライマリレコードに統合されます。</p>
            </div>
          </div>
        </div>

        {/* マージ戦略選択 */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            マージ戦略
          </label>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setMergeStrategy('primary')}
              className={`p-4 rounded-lg border-2 text-left transition-colors ${
                mergeStrategy === 'primary'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
              disabled={isLoading}
            >
              <div className="font-medium text-gray-900 dark:text-white mb-1">プライマリ優先</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                プライマリレコードの情報を優先的に使用します
              </div>
            </button>
            <button
              onClick={() => setMergeStrategy('secondary')}
              className={`p-4 rounded-lg border-2 text-left transition-colors ${
                mergeStrategy === 'secondary'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
              disabled={isLoading}
            >
              <div className="font-medium text-gray-900 dark:text-white mb-1">セカンダリ優先</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                セカンダリレコードの情報を優先的に使用します
              </div>
            </button>
          </div>
        </div>

        {/* フィールド比較 */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <FieldComparison
            label="名前"
            icon={User}
            primaryValue={primaryCustomer.name}
            secondaryValue={secondaryCustomer.name}
            fieldKey="name"
          />
          <FieldComparison
            label="会社名"
            icon={Building2}
            primaryValue={primaryCustomer.companyName}
            secondaryValue={secondaryCustomer.companyName}
            fieldKey="companyName"
          />
          <FieldComparison
            label="電話番号"
            icon={Phone}
            primaryValue={primaryCustomer.phoneNumber}
            secondaryValue={secondaryCustomer.phoneNumber}
            fieldKey="phoneNumber"
          />
          <FieldComparison
            label="メールアドレス"
            icon={Mail}
            primaryValue={primaryCustomer.email}
            secondaryValue={secondaryCustomer.email}
            fieldKey="email"
          />
          <FieldComparison
            label="メモ"
            icon={FileText}
            primaryValue={primaryCustomer.notes}
            secondaryValue={secondaryCustomer.notes}
            fieldKey="notes"
          />
          <TagsComparison />
          <StatsComparison />
        </div>

        {/* フッター */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            disabled={isLoading}
          >
            キャンセル
          </button>
          <button
            onClick={handleMerge}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? 'マージ中...' : 'マージを実行'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CustomerMergeModal
